import { useContext } from "react";
import "./BackgroundSelector.css"
import { GameContext, GameContextType } from "../data/gameState";

/**
 * A list of all of the background assets, as URLs that link to the relevant 
 * file in the public folder. 
 * 
 * These are in groups of 4 based on the color schemes of the sets of images.
 * It's assumed that most screens will be 2 or 4 images wide when displaying a
 * preview.
 */
const BACKGROUND_ASSETS: string[] = [
    "assets/grimoire-backgrounds/red_troublebrewing_logo.webp",
    "assets/grimoire-backgrounds/red_circle_large.webp",
    "assets/grimoire-backgrounds/red_circle_small.webp",
    "assets/grimoire-backgrounds/red_swirls.webp",

    "assets/grimoire-backgrounds/yellow_badmoonrising_logo.webp",
    "assets/grimoire-backgrounds/yellow_circle_large.webp",
    "assets/grimoire-backgrounds/yellow_circle_small.webp",
    "assets/grimoire-backgrounds/yellow_swirls.webp",

    "assets/grimoire-backgrounds/purple_sectsandviolets_logo.webp",
    "assets/grimoire-backgrounds/purple_circle_large.webp",
    "assets/grimoire-backgrounds/purple_circle_small.webp",
    "assets/grimoire-backgrounds/purple_swirls.webp",

    "assets/grimoire-backgrounds/blue_circle_elaborate.png",
    "assets/grimoire-backgrounds/blue_circle_large.webp",
    "assets/grimoire-backgrounds/blue_circle_small.webp",
    "assets/grimoire-backgrounds/blue_swirls.webp",

    "assets/grimoire-backgrounds/green_circle_elaborate.png",
    "assets/grimoire-backgrounds/green_circle_large.webp",
    "assets/grimoire-backgrounds/green_circle_small.webp",
    "assets/grimoire-backgrounds/green_swirls.webp",

    "assets/grimoire-backgrounds/purple_circle_elaborate.png",
    "assets/grimoire-backgrounds/light_green_circle_large.webp",
    "assets/grimoire-backgrounds/light_green_circle_small.webp",
    "assets/grimoire-backgrounds/light_green_swirls.webp",

    "assets/grimoire-backgrounds/orange_circle_elaborate.png",
    "assets/grimoire-backgrounds/pink_circle_large.webp",
    "assets/grimoire-backgrounds/pink_circle_small.webp",
    "assets/grimoire-backgrounds/pink_swirls.webp",

    "assets/grimoire-backgrounds/lounge.webp",
    "assets/grimoire-backgrounds/user_center.webp",
]

/**
 * Display the background selector item. This selector takes up the entire
 * screen, only shows up if a user wants to select a new image, and will
 * automatically close when the user next clicks. If they click on an image,
 * then the background will be set to that image. 
 * 
 * @returns A JSX object to faciliate the above.
 */
export default function BackgroundSelector() {
    const {setGameState, appState, setAppState} = useContext(GameContext) as GameContextType;

    if (!appState.isBackgroundSelectorOpen) return <></>;

    function closeSelector() {
        setAppState(state => {
            return {
                ...state,
                isBackgroundSelectorOpen: false,
            }
        })
    }

    function setBackgroundTo(link: string) {
        setGameState(state => {
            return {
                ...state,
                background: link
            }
        });
    }

    const itemJsx = BACKGROUND_ASSETS.map(link => (
        <div 
            key={link}
            className="BackgroundSelector__item" 
            onClick={() => setBackgroundTo(`url(${link})`)}
            role="button"
        >
            <img
                className="BackgroundSelector__image"
                src={link}
                alt=""
            />
        </div>
    ));

    return (
        <div 
            className="BackgroundSelector__container" 
            style={{ backgroundImage: "url(assets/backgrounds/dark.webp)"}}
            onClick={closeSelector}
            role="button"
        >
            <div className="BackgroundSelector__aligner">
                <div className="BackgroundSelector__content">
                    {itemJsx}
                </div>
            </div>
        </div>
    )
}